/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {TopNavSearchIcon} from '../icons/current/TopNavSearchIcon';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import Render from '../render/Render';
import {dlsColor, dlsDarkAlpha, dlsZIndex, isDarkPalette} from '../theme/theme';
import {DLSTheme, IconSize} from '../types/types';
import FilterTreeChecklistNodeRow from './FilterTreeChecklistNodeRow';
import FilterTreeSearchResults from './FilterTreeSearchResults';
import {
  FilterTreeChecklistChangeBase,
  FilterTreeChecklistHandleBase,
  FilterTreeChecklistPropsBase,
  FilterTreeNodeBase,
  FilterTreeNodeTypeBase,
  FilterTreeSearchResultBase
} from './types';
import useFilterTreeChecklist, {afterNextPaint, DEFAULT_FILTER_TREE_BATCH_SIZE} from './useFilterTreeChecklist';
import {getFilterTreeNodeKey} from './utils';

export type FilterTreeNodeType = FilterTreeNodeTypeBase;

export interface FilterTreeNode extends FilterTreeNodeBase {}

export interface FilterTreeChecklistChange extends FilterTreeChecklistChangeBase {}

export interface FilterTreeChecklistHandle extends FilterTreeChecklistHandleBase {}

export interface FilterTreeChecklistProps extends FilterTreeChecklistPropsBase {}

// the busy overlay's veil, the white token at an alpha of 0x99 out of 0xFF in dark
// mode, otherwise white at six tenths ...
const BUSY_OVERLAY_VEIL = {alphaValue: 0x99 / 255, lightValue: 'rgba(255, 255, 255, 0.6)'};

const useStyles = makeStyles((theme: DLSTheme) => ({
  root : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    padding       : '0 16px 0',
    width         : '100%'
  },
  searchBar : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    flexDirection  : 'column',
    gap            : 10,
    height         : 60,
    justifyContent : 'center',
    padding        : '8px 0',
    width          : '100%'
  },
  searchBarActive : {
    gap    : 4,
    height : 84
  },
  searchBox : {
    alignItems       : 'center',
    backgroundColor  : dlsColor('white'),
    border           : `1px solid ${dlsColor('grey5')}`,
    borderRadius     : 8,
    boxSizing        : 'border-box',
    display          : 'flex',
    gap              : 49,
    height           : 44,
    justifyContent   : 'space-between',
    padding          : 16,
    width            : '100%',
    // the focus ring follows real focus, so clicking away clears it. The
    // border keeps its 1px width (the ring's second pixel is an inset
    // shadow), so focusing never shifts the box's content ...
    '&:focus-within' : {
      borderColor : dlsColor('darkTeal'),
      boxShadow   : `inset 0 0 0 1px ${dlsColor('darkTeal')}`
    }
  },
  searchInputGroup : {
    alignItems : 'center',
    display    : 'flex',
    flex       : 1,
    gap        : 8,
    height     : 24,
    minWidth   : 0
  },
  searchInput : {
    backgroundColor  : 'transparent',
    border           : 0,
    boxSizing        : 'border-box',
    color            : dlsColor('grey3'),
    flex             : 1,
    fontFamily       : 'Inter',
    fontSize         : 14,
    height           : 20,
    lineHeight       : '20px',
    minWidth         : 0,
    outline          : 0,
    padding          : 0,
    width            : '100%',
    '&::placeholder' : {
      color   : dlsColor('grey3'),
      opacity : 1
    }
  },
  clearSearchButton : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    boxSizing       : 'border-box',
    cursor          : 'pointer',
    display         : 'flex',
    flexShrink      : 0,
    height          : 24,
    justifyContent  : 'center',
    padding         : 0,
    width           : 24,
    '& svg'         : {
      height : 16,
      width  : 16
    }
  },
  searchResultsCount : {
    alignSelf    : 'stretch',
    color        : dlsColor('grey2'),
    fontFamily   : 'Inter',
    fontSize     : 13,
    lineHeight   : '16px',
    paddingRight : 4,
    textAlign    : 'right'
  },
  panel : {
    alignItems      : 'flex-start',
    alignSelf       : 'stretch',
    backgroundColor : dlsColor('grey6'),
    borderRadius    : 12,
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    gap             : 12,
    padding         : '16px 8px',
    width           : '100%'
  },
  // the tree spans the whole panel, whatever its rows' widths - so its show
  // all centres in the panel, and its Collapse All keeps its place as groups
  // open and close ...
  treeContent : {
    width : '100%'
  },
  // while a group loads its children (expanding it, or show all) or a picked
  // search result is found, the whole filter - its search included - waits
  // under a light overlay, with the one loader centred over it: a tick or a
  // search made meanwhile would otherwise land late, or move the tree away
  // from where the user was ...
  rootBusy : {
    position : 'relative'
  },
  busyOverlay : {
    alignItems      : 'center',
    backgroundColor : dlsDarkAlpha(isDarkPalette(theme), 'white', BUSY_OVERLAY_VEIL),
    bottom          : 0,
    cursor          : 'progress',
    display         : 'flex',
    justifyContent  : 'center',
    left            : 0,
    position        : 'absolute',
    right           : 0,
    top             : 0,
    zIndex          : 1
  },
  // the one loader, a row under the group being loaded - above the scrim ...
  loaderRow : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    height         : 44,
    justifyContent : 'center',
    position       : 'relative',
    width          : '100%',
    zIndex         : 2
  },
  ctas : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    height         : 44,
    justifyContent : 'space-between',
    padding        : '0 16px',
    width          : '100%'
  },
  ctaGroup : {
    alignItems : 'center',
    display    : 'flex',
    gap        : 8,
    height     : 32
  },
  ctaButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    color           : dlsColor('darkestBlue'),
    cursor          : 'pointer',
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 13,
    fontWeight      : 600,
    height          : 32,
    justifyContent  : 'center',
    letterSpacing   : 0.5,
    lineHeight      : '16px',
    padding         : '4px 16px'
  },
  ctaButtonDisabled : {
    color  : dlsColor('grey4'),
    cursor : 'default'
  },
  // the design's 112px button - its text centred in it, so it ends as far in
  // from the panel's right edge as Select All starts from its left ...
  collapseAllButton : {
    color        : dlsColor('grey1'),
    minWidth     : 112,
    whiteSpace   : 'nowrap',
    '&:disabled' : {
      color  : dlsColor('grey4'),
      cursor : 'default'
    }
  },
  tree : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    gap           : 12,
    width         : '100%'
  },
  row : {
    alignItems      : 'center',
    backgroundColor : dlsColor('grey6'),
    borderRadius    : 8,
    boxSizing       : 'border-box',
    display         : 'flex',
    gap             : 8,
    height          : 32,
    justifyContent  : 'center',
    minWidth        : 280,
    padding         : '4px 8px 4px 16px',
    width           : '100%'
  },
  expander : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    cursor          : 'pointer',
    display         : 'flex',
    height          : 24,
    justifyContent  : 'center',
    padding         : 0,
    width           : 24,
    // an empty group's arrow stays in place, greyed out ...
    '&:disabled'    : {
      cursor  : 'default',
      opacity : 0.4
    }
  },
  expanderWrapper : {
    display    : 'flex',
    flexShrink : 0
  },
  // the empty-group tooltip portals above the filter drawer it sits in -
  // the theme pins every tooltip popper at `100006 !important`, below the
  // drawer, so this outranks it with a doubled selector ...
  expanderTooltipPopper : {
    '&&' : {
      zIndex : `${dlsZIndex('trackingFilterSelectedFiltersConfirmation') + 2} !important` as any
    }
  },
  expanderSpacer : {
    flexShrink : 0,
    height     : 24,
    width      : 24
  },
  arrow : {
    borderBottom : `2px solid ${dlsColor('grey2')}`,
    borderRight  : `2px solid ${dlsColor('grey2')}`,
    height       : 7,
    transform    : 'rotate(-45deg)',
    transition   : 'transform 160ms ease',
    width        : 7
  },
  arrowExpanded : {
    transform : 'rotate(45deg)'
  },
  checkbox : {
    alignItems              : 'center',
    display                 : 'flex',
    flexShrink              : 0,
    height                  : 18,
    justifyContent          : 'center',
    width                   : 18,
    '& .MuiButtonBase-root' : {
      padding : 0
    },
    '& .MuiFormControlLabel-root' : {
      height : 18,
      margin : 0,
      width  : 18
    }
  },
  assetIcon : {
    alignItems     : 'center',
    display        : 'flex',
    flexShrink     : 0,
    height         : 24,
    justifyContent : 'center',
    width          : 24
  },
  label : {
    color        : dlsColor('grey1'),
    flex         : 1,
    fontFamily   : 'Inter',
    fontSize     : 14,
    fontWeight   : 500,
    lineHeight   : '20px',
    minWidth     : 0,
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    whiteSpace   : 'nowrap'
  },
  groupLabel : {
    color      : dlsColor('grey1'),
    fontWeight : 500
  },
  children : {
    overflow   : 'hidden',
    transition : 'height 160ms ease'
  },
  showAllRow : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    height         : 44,
    justifyContent : 'center',
    padding        : '0 16px',
    width          : '100%'
  },
  showAllButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    borderRadius    : 4,
    color           : dlsColor('darkestBlue'),
    cursor          : 'pointer',
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 13,
    fontWeight      : 600,
    gap             : 8,
    height          : 32,
    letterSpacing   : 0.5,
    lineHeight      : '16px',
    padding         : '0 8px',
    '&:disabled'    : {
      color  : dlsColor('grey4'),
      cursor : 'default'
    }
  },
  empty : {
    color      : dlsColor('grey3'),
    fontFamily : 'Inter',
    fontSize   : 14,
    lineHeight : '20px',
    padding    : '8px 16px 16px',
    width      : '100%'
  }
}));

// how long a revealed result's loader waits on its row to be scrolled to ...
const REVEAL_SCROLL_TIMEOUT_MS = 1000;

interface FilterTreeChecklistPanelProps {
  batchSize: number;
  classes: Record<string, string>;
  clear: () => void;
  clearDisabled: boolean;
  clearLabel: string;
  collapseAll: () => void;
  collapseAllDisabled: boolean;
  collapseAllLabel: string;
  disabled: boolean;
  displayedNodes: FilterTreeNodeBase[];
  effectiveExpandedIds: Set<string>;
  loaderNodeKey: string | null;
  loadingLabel: string;
  noSearchResults: boolean;
  noSearchResultsLabel: string;
  onSelectSearchResult: (result: FilterTreeSearchResultBase) => void;
  revealMore: (node: FilterTreeNodeBase) => void;
  // the tree itself is filtered by the search (filter search mode) - every
  // matching child shows, so 'show all' paging is off ...
  treeFiltered: boolean;
  searchResults: FilterTreeSearchResultBase[];
  searchResultsLoading: boolean;
  selectAll: () => void;
  selectAllDisabled: boolean;
  selectAllLabel: string;
  selected: Set<string>;
  showAllLabel: string;
  showLessLabel: string;
  showSearchResults: boolean;
  toggleExpand: (node: FilterTreeNodeBase) => void;
  toggleNode: (node: FilterTreeNodeBase) => void;
  visibleCounts: Record<string, number>;
  visibleRootNodes: FilterTreeNodeBase[];
}

/**
 * Renders either the search results list or the CTA bar + tree, for the
 * body of a tree checklist filter's panel.
 */
const FilterTreeChecklistPanel = ({
  batchSize,
  classes,
  clear,
  clearDisabled,
  clearLabel,
  collapseAll,
  collapseAllDisabled,
  collapseAllLabel,
  disabled,
  displayedNodes,
  effectiveExpandedIds,
  loaderNodeKey,
  loadingLabel,
  noSearchResults,
  noSearchResultsLabel,
  onSelectSearchResult,
  revealMore,
  searchResults,
  searchResultsLoading,
  selectAll,
  selectAllDisabled,
  selectAllLabel,
  selected,
  showAllLabel,
  showLessLabel,
  showSearchResults,
  toggleExpand,
  toggleNode,
  treeFiltered,
  visibleCounts,
  visibleRootNodes
}: FilterTreeChecklistPanelProps) => (
  <Render>
    <Render.If condition={showSearchResults}>
      <FilterTreeSearchResults results={searchResults}
        loading={searchResultsLoading}
        loadingLabel={loadingLabel}
        noResultsLabel={noSearchResultsLabel}
        onSelect={onSelectSearchResult} />
    </Render.If>
    <Render.Else>
      <div className={classes.ctas}>
        <div className={classes.ctaGroup}>
          <button type='button'
            className={classnames(classes.ctaButton, {
              [classes.ctaButtonDisabled] : selectAllDisabled
            })}
            disabled={selectAllDisabled}
            onClick={selectAll}>
            {selectAllLabel}
          </button>
          <button type='button'
            className={classnames(classes.ctaButton, {
              [classes.ctaButtonDisabled] : clearDisabled
            })}
            disabled={clearDisabled}
            onClick={clear}>
            {clearLabel}
          </button>
        </div>
        <button type='button'
          className={classnames(classes.ctaButton, classes.collapseAllButton, {
            [classes.ctaButtonDisabled] : collapseAllDisabled
          })}
          disabled={collapseAllDisabled}
          onClick={collapseAll}>
          {collapseAllLabel}
        </button>
      </div>
      <div className={classes.tree} role='tree'>
        <Render.If condition={!displayedNodes.length}>
          <div className={classes.empty} role='status'>
            {translate(noSearchResults
              ? 'filters:options.noResults'
              : 'filters:options.noOptions')}
          </div>
        </Render.If>
        {visibleRootNodes.map(node => (
          <FilterTreeChecklistNodeRow key={getFilterTreeNodeKey(node)}
            batchSize={batchSize}
            classes={classes}
            disabled={disabled}
            expandedIds={effectiveExpandedIds}
            level={0}
            loaderNodeKey={loaderNodeKey}
            loadingLabel={loadingLabel}
            node={node}
            onRevealMore={revealMore}
            onToggle={toggleNode}
            onToggleExpand={toggleExpand}
            paginationDisabled={treeFiltered}
            selected={selected}
            showAllLabel={showAllLabel}
            showLessLabel={showLessLabel}
            visibleCounts={visibleCounts} />
        ))}
      </div>
    </Render.Else>
  </Render>
);

/**
 * Displays a searchable, expandable tree checklist for hierarchical filters.
 *
 * ```typescript
 * import FilterTreeChecklist from '@ion/dls/src/filters/FilterTreeChecklist';
 *
 * ...
 *
 * <FilterTreeChecklist ref={treeRef}
 *   nodes={nodes}
 *   selectedIds={selectedIds}
 *   searchPlaceholder='Search groups or assets...'
 *   onLoadChildren={loadChildren}
 *   onChange={handleTreeFilterChange} />
 * ```
 */
export const FilterTreeChecklist = forwardRef<FilterTreeChecklistHandleBase, FilterTreeChecklistProps>(({
  onChange,
  onLoadChildren,
  onSearchTermChange,
  className,
  emptyState,
  nodes = [],
  disabled = false,
  searchMode = 'filter',
  minSearchLength = 0,
  minSearchLengthLabel,
  searchResults = [],
  searchResultsLoading = false,
  selectedIds = [],
  loadingLabel = translate('common:states.loading'),
  batchSize = DEFAULT_FILTER_TREE_BATCH_SIZE,
  clearLabel = translate('filters:options.clear'),
  collapseAllLabel = translate('filters:drawer.collapseAll'),
  noSearchResultsLabel = translate('filters:options.noResults'),
  searchPlaceholder = translate('filters:options.searchPlaceholder'),
  selectAllLabel = translate('filters:options.selectAll'),
  showAllLabel = translate('filters:options.showAll'),
  showLessLabel = translate('filters:options.showLess')
}: FilterTreeChecklistProps, ref) => {
  const classes = useStyles();
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const {
    activateSearch,
    allVisibleSelected,
    blurSearch,
    busyNodeKey,
    clear,
    clearPendingScroll,
    closeSearch,
    collapseAll,
    displayedNodes,
    effectiveExpandedIds,
    handleSearchChange,
    hasExpandedNodes,
    isLoadingChildren,
    pendingScrollKey,
    resultsSearchMode,
    revealMore,
    revealNodePath,
    searchActive,
    searchResultCount,
    searchTerm,
    selectAll,
    selected,
    toggleExpand,
    toggleNode,
    visibleCounts,
    visibleRootNodes
  } = useFilterTreeChecklist({
    batchSize,
    nodes,
    onChange,
    onLoadChildren,
    onSearchTermChange,
    searchMode,
    selectedIds
  });
  const hasSearchTerm        = searchActive && !!searchTerm.trim();
  const showSearchResults    = resultsSearchMode && hasSearchTerm;
  // a results search too short to run yet asks for more characters, rather
  // than reporting no results ...
  const searchTooShort       = !!minSearchLengthLabel && searchTerm.trim().length < minSearchLength;
  const noSearchResults      = !resultsSearchMode && searchActive && !displayedNodes.length;
  const selectAllDisabled    = disabled || !displayedNodes.length || allVisibleSelected;
  const clearDisabled        = disabled || !selectedIds.length || noSearchResults;
  const collapseAllDisabled  = disabled || !hasExpandedNodes;
  const emptyStateVisible    = !nodes.length && !!emptyState;
  const displayedResultCount = resultsSearchMode ? searchResults.length : searchResultCount;
  const resultCountLabel     = translate(
    `filters:options.${displayedResultCount === 1 ? 'result' : 'results'}`,
    {count: displayedResultCount}
  );

  // re-checks whenever the rendered tree changes (not just when
  // pendingScrollKey itself changes) because a freshly revealed node -
  // one spliced in by onSelectSearchResult, or loaded while expanding an
  // ancestor - may not exist in the DOM yet on the render that first sets
  // pendingScrollKey...
  useEffect(() => {
    if (!pendingScrollKey) {
      return;
    }
    const rowElement = treeContainerRef.current?.querySelector(`[data-node-key="${pendingScrollKey}"]`);
    if (rowElement) {
      rowElement.scrollIntoView({block: 'center', inline: 'nearest'});
      clearPendingScroll();
    }
  }, [clearPendingScroll, displayedNodes, pendingScrollKey]);

  // a picked result shows a loader until it is ticked and scrolled into view,
  // rather than the tree visibly expanding, loading and scrolling to it -
  // however long finding it takes. 'found' once the reveal resolves, and
  // done once the scroll above has run (or, should the row never render,
  // shortly after) ...
  const [revealState, setRevealState] = useState<'idle' | 'finding' | 'found'>('idle');
  const isRevealing = revealState !== 'idle';
  const isBusy      = isLoadingChildren || isRevealing;
  // the loader shows under the group being loaded - or under the tree's own
  // top group, while a picked result is found ...
  const loaderNodeKey = isBusy
    ? busyNodeKey ?? (visibleRootNodes[0] ? getFilterTreeNodeKey(visibleRootNodes[0]) : null)
    : null;

  // the loader is painted before the reveal starts - a target already loaded
  // is revealed without waiting on anything, and its whole (heavy) render ran
  // before the loader ever showed ...
  const revealWithLoader = useCallback(async(nodePath: string[]) => {
    setRevealState('finding');
    try {
      await afterNextPaint();
      await revealNodePath(nodePath);
    } finally {
      setRevealState('found');
    }
  }, [revealNodePath]);

  const handleSelectSearchResult = useCallback(async(result: FilterTreeSearchResultBase) => {
    closeSearch();
    await revealWithLoader(result.nodePath);
  }, [closeSearch, revealWithLoader]);

  // a node revealed from outside (e.g. the global search) shows the same loader ...
  useImperativeHandle(ref, () => ({revealNodePath: revealWithLoader}), [revealWithLoader]);

  useEffect(() => {
    let fallback: ReturnType<typeof setTimeout>;
    if (revealState === 'found') {
      if (pendingScrollKey) {
        fallback = setTimeout(function() {
          setRevealState('idle');
        }, REVEAL_SCROLL_TIMEOUT_MS);
      } else {
        setRevealState('idle');
      }
    }
    return function() {
      clearTimeout(fallback);
    };
  }, [pendingScrollKey, revealState]);

  return (
    <div ref={treeContainerRef}
      className={classnames(classes.root, className, {[classes.rootBusy]: isBusy})}>
      <Render.If condition={isBusy}>
        <div className={classes.busyOverlay} role='status' aria-label={loadingLabel} />
      </Render.If>
      <Render.If condition={!emptyStateVisible}>
        {/* the bar only grows for the result count under a typed term -
            focusing the empty box leaves it where it is ... */}
        <div className={classnames(classes.searchBar, {
          [classes.searchBarActive] : hasSearchTerm
        })}>
          <div className={classes.searchBox}>
            <div className={classes.searchInputGroup}>
              <TopNavSearchIcon size={IconSize.medium} color={dlsColor('grey2')} />
              <input className={classes.searchInput}
                aria-label={searchPlaceholder}
                value={searchTerm}
                placeholder={searchPlaceholder}
                onFocus={activateSearch}
                onBlur={blurSearch}
                onChange={handleSearchChange} />
            </div>
            <Render.If condition={searchActive}>
              <button type='button'
                className={classes.clearSearchButton}
                aria-label={translate('filters:options.closeSearch')}
                onClick={closeSearch}>
                <MaterialExitCloseIcon size={IconSize.small} />
              </button>
            </Render.If>
          </div>
          <Render.If condition={hasSearchTerm}>
            <div className={classes.searchResultsCount} aria-live='polite'>
              {resultCountLabel}
            </div>
          </Render.If>
        </div>
      </Render.If>
      <Render>
        <Render.If condition={emptyStateVisible}>
          {emptyState}
        </Render.If>
        <Render.Else>
          <div className={classes.panel} aria-busy={isBusy}>
            <div className={classes.treeContent}>
              <FilterTreeChecklistPanel batchSize={batchSize}
                classes={classes}
                clear={clear}
                clearDisabled={clearDisabled}
                clearLabel={clearLabel}
                collapseAll={collapseAll}
                collapseAllDisabled={collapseAllDisabled}
                collapseAllLabel={collapseAllLabel}
                disabled={disabled}
                displayedNodes={displayedNodes}
                effectiveExpandedIds={effectiveExpandedIds}
                loaderNodeKey={loaderNodeKey}
                loadingLabel={loadingLabel}
                noSearchResults={noSearchResults}
                noSearchResultsLabel={searchTooShort ? minSearchLengthLabel : noSearchResultsLabel}
                onSelectSearchResult={handleSelectSearchResult}
                revealMore={revealMore}
                treeFiltered={searchActive && !resultsSearchMode}
                searchResults={searchResults}
                searchResultsLoading={searchResultsLoading}
                selectAll={selectAll}
                selectAllDisabled={selectAllDisabled}
                selectAllLabel={selectAllLabel}
                selected={selected}
                showAllLabel={showAllLabel}
                showLessLabel={showLessLabel}
                showSearchResults={showSearchResults}
                toggleExpand={toggleExpand}
                toggleNode={toggleNode}
                visibleCounts={visibleCounts}
                visibleRootNodes={visibleRootNodes} />
            </div>
          </div>
        </Render.Else>
      </Render>
    </div>
  );
});

export default memo(FilterTreeChecklist);
